import React, { useState, useEffect, useContext } from 'react';
import AutoRefreshControl from '../components/AutoRefreshControl';
import TradingTable from '../components/TradingTable';
import { createFavoriteApiService } from '../utils/favoriteApiService';
import { useFavorites } from '../contexts/FavoriteContext';
import { useJWTAuth } from '../context/JWTAuthContext';
import { ServerConfigContext } from '../context/ServerConfigContext';
import { createApiService } from '../utils/apiService';
import { formatPrice, formatRate, formatMarketCap } from '../utils/formatUtils';
import './TradingPages.css';

const FavoritesPage = () => {
  const [favoriteData, setFavoriteData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate] = useState(new Date().toISOString().split('T')[0]); // 오늘 날짜
  const [visibleDetails, setVisibleDetails] = useState({
    sector: true,
    per: true,
    pbr: true,
    foreignBuy: true,
    institutionBuy: true,
    individualBuy: true,
    high52Week: true,
    low52Week: true,
    volume52Week: true,
  });

  // 기본 차트 설정
  const defaultChartSettings = {
    upColor: "#d32f2f",
    downColor: "#1976d2",
    wickColor: "#424242",
    wickThickness: 1,
    bodyThickness: 0.8,
    showMA5: true,
    showMA10: true,
    showMA20: true,
    showMA60: false,
    showMA120: false,
    ma5Color: "#ff9800",
    ma10Color: "#4caf50",
    ma20Color: "#9c27b0",
    ma60Color: "#f44336",
    ma120Color: "#795548",
    showGrid: true,
    gridColor: "#e0e0e0",
  };

  const [chartSettings, setChartSettings] = useState(defaultChartSettings);
  const [activeTab, setActiveTab] = useState("chart");
  const [showDetailsSection, setShowDetailsSection] = useState(true);

  const { favoriteStocks, loadFavorites } = useFavorites();
  const { apiCall, isAuthenticated, isLoading: authLoading } = useJWTAuth();
  const { getServer } = useContext(ServerConfigContext);
  const favoriteApi = createFavoriteApiService(apiCall);

  const fetchFavoriteData = async () => {
    setLoading(true);
    setError(null);

    try {
      // 1. 즐겨찾기 목록 가져오기
      const favorites = await favoriteApi.getFavorites();

      if (favorites.length === 0) {
        setFavoriteData([]);
        setLoading(false);
        return;
      }

      // stock-api-server 설정 가져오기
      const stockServer = getServer("stock-api-server");
      const stockApiService = createApiService(stockServer);

      // 2. stock-db-api에서 테마 정보, stock-api에서 현재가 정보 가져오기
      const stockDataPromises = favorites.map(async (fav) => {
        try {
          // stock-db-api의 /api/v1/themes/:stock_code 엔드포인트 사용
          const themeInfo = await apiCall(`/stock-db-api/api/v1/themes/${fav.stock_code}`, {
            method: 'GET',
          });

          // themes를 ;로 split하여 배열로 변환
          let themesArray = [];
          if (themeInfo.themes) {
            if (Array.isArray(themeInfo.themes)) {
              themesArray = themeInfo.themes;
            } else if (typeof themeInfo.themes === 'string') {
              // 문자열인 경우 세미콜론(;)으로 분리
              themesArray = themeInfo.themes.split(';').map(t => t.trim()).filter(t => t);
            }
          }

          // stock-api에서 현재가 정보 가져오기
          let currentPrice = '-';
          let rate = '-';
          let marketCap = '-';

          try {
            const priceData = await stockApiService.get(`/api/v1/stocks/current-price`, {
              shcode: fav.stock_code
            });

            // 현재가, 등락률, 시총 추출 및 포맷팅
            if (priceData && priceData.data) {
              currentPrice = formatPrice(priceData.data.price);
              rate = formatRate(priceData.data.diff);
              // total을 억원으로 변환 후 포맷팅
              const totalInMillion = parseFloat(priceData.data.total) || 0;
              const totalInBillion = totalInMillion; // 이미 억원 단위
              marketCap = formatMarketCap(totalInBillion);
            }
          } catch (priceErr) {
            console.error(`Failed to fetch current price for ${fav.stock_code}:`, priceErr);
            // 현재가 조회 실패 시 기본값 유지
          }

          return {
            stock_code: fav.stock_code,
            stock_name: themeInfo.stock_name || '정보 없음',
            themes: themesArray,
            favorited_at: fav.created_at,
            price: currentPrice,  // 현재가
            rate: rate,           // 등락률
            total: marketCap,     // 시총
          };
        } catch (err) {
          console.error(`Failed to fetch data for ${fav.stock_code}:`, err);
          return {
            stock_code: fav.stock_code,
            stock_name: '정보 없음',
            themes: [],
            price: '-',
            rate: '-',
            total: '-',
            error: true,
          };
        }
      });

      const stockData = await Promise.all(stockDataPromises);
      setFavoriteData(stockData);
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
      setError('즐겨찾기 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 인증 로딩이 완료되고, 인증된 경우에만 데이터 로드
    if (!authLoading && isAuthenticated) {
      console.log('✅ [FavoritesPage] 인증 완료, 데이터 로드 시작');
      fetchFavoriteData();
    } else if (!authLoading && !isAuthenticated) {
      console.log('ℹ️ [FavoritesPage] 로그인 필요');
      setError('로그인이 필요합니다.');
    }
    // eslint-disable-next-line
  }, [authLoading, isAuthenticated]); // 인증 상태가 변경될 때 실행

  const handleRefresh = () => {
    loadFavorites(); // FavoriteContext 새로고침
    fetchFavoriteData(); // 상세 데이터 새로고침
  };

  if (loading && favoriteData.length === 0) {
    return <div style={{ padding: '20px' }}>로딩 중...</div>;
  }

  if (error) {
    return <div style={{ padding: '20px', color: 'red' }}>{error}</div>;
  }

  if (favoriteData.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>즐겨찾기</h2>
        <p>즐겨찾기한 종목이 없습니다.</p>
        <p>테마 페이지에서 별(☆) 버튼을 클릭하여 종목을 추가하세요.</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <style>{`
        /* 즐겨찾기 페이지에서는 테마 컬럼을 항상 표시 */
        .page-container .trading-table th.theme,
        .page-container .trading-table td.theme {
          display: table-cell !important;
        }

        .page-container .trading-table th.star,
        .page-container .trading-table td.star {
          display: table-cell !important;
        }

        /* 거래대금과 거래량 컬럼 숨김 */
        .page-container .trading-table th.trading-value,
        .page-container .trading-table td.trading-value,
        .page-container .trading-table th.volume,
        .page-container .trading-table td.volume {
          display: none !important;
        }
      `}</style>

      {/* 제어 패널 */}
      <div className="datetime-control-panel">
        <div className="page-title-section">
          <h1 className="page-title">즐겨찾기</h1>
          <p className="stock-count">즐겨찾기한 종목 {favoriteData.length}개</p>
        </div>
        <div style={{ flex: 1 }}></div>
        <AutoRefreshControl onRefresh={handleRefresh} />
      </div>

      <TradingTable
        key="favorites-trading-table"
        pageId="FavoritesPage"
        data={favoriteData.map((item, index) => ({
            rank: index + 1,
            name: item.stock_name,
            price: item.price,  // 이미 포맷팅됨
            priceValue: parseFloat(item.price?.replace(/,/g, '')) || 0,
            rate: item.rate,  // 이미 포맷팅됨
            rateValue: parseFloat(item.rate?.replace(/[+%]/g, '')) || 0,
            tradingValue: '-',
            tradingValueRaw: 0,
            volume: '-',
            marketCap: item.total,  // 이미 포맷팅됨
            themes: item.themes,
            theme: item.themes?.join(', ') || '',
            shcode: item.stock_code,
            rawData: item,
        }))}
        isLoading={loading && favoriteData.length === 0}
        loadingMessage="즐겨찾기 데이터를 불러오는 중..."
        showStarButton={true}
        hiddenColumns={['trading-value', 'volume']}
        visibleDetails={visibleDetails}
        setVisibleDetails={setVisibleDetails}
        chartSettings={chartSettings}
        setChartSettings={setChartSettings}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedDate={selectedDate}
        showDetailsSection={showDetailsSection}
        setShowDetailsSection={setShowDetailsSection}
        emptyMessage={
          favoriteData.length === 0 && !loading
            ? "즐겨찾기한 종목이 없습니다.\n테마 페이지에서 별(☆) 버튼을 클릭하여 종목을 추가하세요."
            : undefined
        }
      />
    </div>
  );
};

export default FavoritesPage;
